import { Browser } from '@capacitor/browser'
import {
  Avatar,
  Badge,
  Button,
  Container,
  Grid,
  List,
  ListSubheader,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material'
import { MouseEvent, useMemo, useState } from 'react'
import {
  IconButton,
  Icons,
  InformationModal,
  MenuDrawer,
  MenuListItem,
} from 'utoniq-ui'

import { ArtistPaymentType } from '@/clients/utoniq-core/schema'
import { AppThemeProvider, styles } from '@/components/styles'
import { config, constants } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { Organism } from '@/core/architecture'
import { isAvailableLiveStream } from '@/core/liveStream'
import { getPlatform } from '@/core/native/app'
import { filterHiddenItem } from '@/lib/array'

import { useGetArtistSideMenuInfo } from '../ConsoleBaseLayout/useGetArtistSideMenuInfo'

export type ArtistSideMenuProps = {
  open: boolean
  onClose: () => void
}

// 画面幅 md 未満で使用するトグル可能なサイドメニュー
export const ArtistSideMenu: Organism<ArtistSideMenuProps> = ({
  open = false,
  onClose,
}) => {
  return (
    <AppThemeProvider>
      <MenuDrawer
        anchor="left"
        open={open}
        onClose={onClose}
        sx={theme => ({
          [theme.breakpoints.up('md')]: {
            display: 'none',
          },
        })}
        PaperProps={{
          sx: {
            pt: styles.safeAreaTop,
            pb: styles.safeAreaBottom,
            backgroundImage: 'none',
            left: 'initial',
          },
        }}
      >
        <SideMenuContainer />
      </MenuDrawer>
    </AppThemeProvider>
  )
}

// 画面幅 md 以上で常時表示するサイドメニュー
export const ArtistSideMenuForPC: Organism<
  Pick<ArtistSideMenuProps, 'onClose'>
> = ({ onClose }) => {
  return (
    <AppThemeProvider>
      <MenuDrawer
        anchor="left"
        onClose={onClose}
        sx={theme => ({
          width: '100%',
          [theme.breakpoints.down('md')]: {
            display: 'none',
          },
        })}
        PaperProps={{
          sx: {
            borderRight: 'none',
          },
        }}
        // Drawer を常時表示するための指定
        variant="permanent"
      >
        <SideMenuContainer />
      </MenuDrawer>
    </AppThemeProvider>
  )
}

// サイドメニュー本体
const SideMenuContainer: Organism<Record<string, never>> = () => {
  const {
    user: currentUser,
    paymentType,
    hasArrangingDeliveryItems,
    membership,
    enableDmRoom,
  } = useGetArtistSideMenuInfo()
  const { t } = useTranslation('artistSideMenu')
  const { routerPages, router, generatePath } = useRouter()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const isWeb = getPlatform() === 'web'
  const [openInNativeLiveStreamModal, setOpenInNativeLiveStreamModal] =
    useState(false)
  const listService = useMemo(
    () => [
      {
        title: t('home'),
        Icon: (
          <IconButton.Home
            href={routerPages.ConsoleDashboard}
            sx={{ color: 'grey.700' }}
            bgcolor="grey.50"
          />
        ),
      },
      {
        title: t('officialInfo'),
        Icon: (
          <IconButton.Info
            size="medium"
            href={routerPages.ConsoleOfficialInformations}
            sx={{ color: 'grey.700' }}
            bgcolor="grey.50"
          />
        ),
      },
      {
        title: t('posts'),
        Icon: (
          <IconButton.Post
            size="medium"
            href={routerPages.ConsolePost}
            sx={{ color: 'grey.700' }}
            bgcolor="grey.50"
          />
        ),
      },
      {
        title: t('items'),
        hidden: !config.modules.item,
        Icon: (
          <IconButton.OwnedItems
            href={routerPages.ConsoleItems}
            sx={{ color: 'grey.700' }}
            bgcolor="grey.50"
          />
        ),
      },
      {
        title: t('collections'),
        hidden: !config.modules.item,
        Icon: (
          <IconButton.Collection
            href={routerPages.ConsoleCollections}
            size="medium"
            sx={{ color: 'grey.700' }}
            bgcolor="grey.50"
          />
        ),
      },
      {
        title: t('missions'),
        hidden: !config.modules.mission,
        Icon: (
          <IconButton.Mission
            href={routerPages.ConsoleMissions}
            sx={{ color: 'grey.700' }}
            bgcolor="grey.50"
          />
        ),
      },
      // {
      //   title: t('communityManagement'),
      //   href:routerPages.MyOwnedItems,
      //   Icon: <Icons.Thread />,
      // },
      {
        title: t('DmRooms'),
        hidden: !enableDmRoom,
        Icon: (
          <IconButton.DM
            href={routerPages.ConsoleDmRooms}
            sx={{ color: 'grey.700' }}
            bgcolor="grey.50"
          />
        ),
      },
      {
        title: t('liveStream'),
        hidden: !isAvailableLiveStream(currentUser?.uid),
        Icon: (
          <IconButton.Live
            onClick={() => {
              if (isWeb)
                router.push(generatePath(routerPages.ConsoleLiveSetting))

              setOpenInNativeLiveStreamModal(true)
            }}
            sx={{ color: 'grey.700' }}
            bgcolor="grey.50"
          />
        ),
      },
      {
        hidden: !config.modules.event,
        title: t('events'),
        Icon: (
          <IconButton.ShokufesEvent
            href={routerPages.ConsoleShokufesEvents}
            sx={{ color: 'grey.700' }}
            bgcolor="grey.50"
          />
        ),
      },
      {
        title: t('data'),
        Icon: (
          <IconButton.Level
            href={routerPages.ConsoleData}
            sx={{ color: 'grey.700' }}
            bgcolor="grey.50"
          />
        ),
      },
      {
        title: t('setting'),
        Icon: (
          <IconButton.Setting
            href={routerPages.ConsoleAccountSetting}
            sx={{ color: 'grey.700' }}
            bgcolor="grey.50"
          />
        ),
      },
    ],
    [
      currentUser?.uid,
      generatePath,
      isWeb,
      router,
      routerPages.ConsoleAccountSetting,
      routerPages.ConsoleCollections,
      routerPages.ConsoleDashboard,
      routerPages.ConsoleData,
      routerPages.ConsoleDmRooms,
      routerPages.ConsoleItems,
      routerPages.ConsoleLiveSetting,
      routerPages.ConsoleMissions,
      routerPages.ConsolePost,
      routerPages.ConsoleShokufesEvents,
      t,
      enableDmRoom,
    ]
  )

  const earnings = useMemo(
    () => [
      {
        title: t('orders'),
        href: routerPages.ConsoleOrders,
        Icon: <Icons.Cart sx={{ color: 'grey.700' }} />,
      },
      {
        title: t('customerInfo'),
        href: routerPages.ConsoleUsers,
        Icon: <Icons.User sx={{ color: 'grey.700' }} />,
      },
      {
        title: t('revenue', '収益管理'),
        // Billingアカウントの場合、収益管理は見えないようにする。
        hidden: !paymentType || paymentType === ArtistPaymentType.Billing,
        href: routerPages.ConsoleRevenue,
        Icon: <Icons.Money sx={{ color: 'grey.700' }} />,
      },
      {
        title: t('membership'),
        hidden: !membership && paymentType === ArtistPaymentType.Billing,
        href: routerPages.ConsoleMembership,
        Icon: <Icons.Group sx={{ color: 'grey.700' }} />,
      },
      {
        title: t('delivery'),
        href: routerPages.ConsoleDeliveryManagement,
        Icon: (
          <Badge
            badgeContent={hasArrangingDeliveryItems ? '' : undefined}
            color={'like' as any}
            sx={{
              '& > span': {
                top: 0,
                right: 0,
                minWidth: 12,
                height: 12,
                bgcolor: 'like.tap',
              },
            }}
          >
            <Icons.Delivery sx={{ color: 'grey.700' }} />
          </Badge>
        ),
        secondaryAction: hasArrangingDeliveryItems
          ? t('hasItemWaitingDelivery')
          : '',
      },
    ],
    [
      hasArrangingDeliveryItems,
      paymentType,
      routerPages.ConsoleDeliveryManagement,
      routerPages.ConsoleMembership,
      routerPages.ConsoleOrders,
      routerPages.ConsoleRevenue,
      routerPages.ConsoleUsers,
      t,
    ]
  )

  const secondCreation = useMemo(
    () => [
      {
        title: t('derivativeWorks'),
        href: '#',
        Icon: <Icons.Image sx={{ color: 'grey.700' }} />,
      },
      {
        title: t('rights'),
        href: '#',
        Icon: <Icons.Right sx={{ color: 'grey.700' }} />,
      },
    ],
    [t]
  )

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const mobileSideMenuWidth = constants.ui.layout.mobileSideMenuWidth
  const desktopSideMenuWidth = constants.ui.layout.desktopSideMenuWidth

  return (
    <>
      <Container
        sx={{
          width: { xs: mobileSideMenuWidth, md: desktopSideMenuWidth },
          bgcolor: 'background.default',
        }}
        disableGutters
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ py: 3, px: 5 }}
        >
          <Stack direction="row" alignItems="center" spacing={3}>
            <Avatar
              src={currentUser?.avatarUrl || ''}
              sx={{ width: 36, height: 36 }}
            />
            <Typography variant="h6" sx={{ fontSize: { md: 14 } }}>
              {currentUser?.displayName}
            </Typography>
          </Stack>
          <IconButton.Pencil
            size="small"
            color="grey.700"
            bgcolor="grey.50"
            onClick={handleClick}
          />
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem
              onClick={() => {
                handleClose()
                router.push(routerPages.ConsoleMyProfileEdit)
              }}
              sx={{ width: 200, py: 3 }}
            >
              {t('consoleMyProfileEdit')}
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleClose()
                router.push(routerPages.ConsoleCampaignBannerSetting)
              }}
              sx={{ width: 200, py: 3 }}
            >
              {t('consoleCampaignBannerSetting')}
            </MenuItem>
          </Menu>
        </Stack>
        <Grid container rowSpacing={5} columnSpacing={1} sx={{ py: 3, px: 5 }}>
          {filterHiddenItem(listService).map(({ Icon, title }, index) => (
            <Grid item xs={3} key={index}>
              <Stack flex={1} alignItems="center" spacing={2}>
                {Icon}
                <Typography variant="overline" color="text.secondary">
                  {title}
                </Typography>
              </Stack>
            </Grid>
          ))}
        </Grid>
        {(config.modules.membership ||
          paymentType === ArtistPaymentType.Connect ||
          config.modules.preSale) && (
          <List
            subheader={
              <ListSubheader
                sx={{
                  pb: '2px',
                  pt: 4,
                  px: 5,
                  bgcolor: 'background.default',
                }}
              >
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ fontSize: { md: 12 } }}
                >
                  {t('earnings')}
                </Typography>
              </ListSubheader>
            }
          >
            {filterHiddenItem(earnings).map(
              ({ href, title, Icon, secondaryAction }, index) => (
                <MenuListItem
                  key={index}
                  href={href}
                  title={title}
                  icon={Icon}
                  sx={{ px: 5 }}
                  primaryTypographyProps={{ fontSize: { md: 16 } }}
                  secondaryAction={
                    <Typography variant="overline" color="like.tap">
                      {secondaryAction}
                    </Typography>
                  }
                />
              )
            )}
          </List>
        )}
        {/**
         * @description
         * Open when function is implemented in the future
         * https://app.clickup.com/t/908127/UT-8544
         */}
        {config.modules.nft && false && (
          <List
            subheader={
              <ListSubheader
                sx={{
                  pb: '2px',
                  pt: 4,
                  px: 5,
                  bgcolor: 'background.default',
                }}
              >
                <Typography variant="subtitle2" color="text.secondary">
                  {t('secondCreation')}
                </Typography>
              </ListSubheader>
            }
          >
            {secondCreation.map(({ href, title, Icon }, index) => (
              <MenuListItem
                key={index}
                href={href}
                title={title}
                icon={Icon}
                sx={{ px: 5 }}
              />
            ))}
          </List>
        )}
      </Container>
      <InformationModal
        open={openInNativeLiveStreamModal}
        onClose={() => setOpenInNativeLiveStreamModal(false)}
        title={t('inNativeLiveStreamModalTitle')}
        content={t('inNativeLiveStreamModalContent')}
        buttons={
          <Button
            size="large"
            fullWidth
            onClick={() => {
              Browser.open({
                url: `${config.domain}${routerPages.ConsoleLiveSetting}`,
              }).then(() => setOpenInNativeLiveStreamModal(false))
            }}
          >
            {t('openBrowser')}
          </Button>
        }
        cancelText={t('close')}
      />
    </>
  )
}
